"use client";
import React from 'react';
import { motion } from 'framer-motion';
import { useTranslations } from 'next-intl';
import { Link } from '@/navigation';
import Image from 'next/image';

interface Category {
  id: string;
  slug: string;
  name: string;
  sortNumber?: string | null;
  metadata?: Array<{
    key: string;
    value: string;
  }>;
  translation?: {
    name: string;
    description?: string;
  };
  media?: string | null;
  children?: {
    edges: Array<{
      node: Category;
    }>;
  };
  products?: {
    totalCount: number;
  };
}

interface CategoryGridProps {
  categories: Category[];
  onCategoryClick?: (category: Category) => void;
  showBackButton?: boolean;
  onBackClick?: () => void;
  parentCategoryName?: string;
  isFirstLevel?: boolean;
}

export default function CategoryGrid({ 
  categories, 
  onCategoryClick,
  showBackButton = false,
  onBackClick,
  parentCategoryName,
  isFirstLevel = false
}: CategoryGridProps) {
  const t = useTranslations('plsyy');

  const handleCategoryClick = (category: Category) => {
    if (onCategoryClick) {
      onCategoryClick(category);
    }
  };

  // 对分类进行排序 - 按照 metadata 中的 sortNumber 排序
  const sortedCategories = React.useMemo(() => {
    return [...categories].sort((a, b) => {
      // 从 metadata 数组中获取 sortNumber
      const getSortNumber = (category: Category): number | null => {
        if (category.metadata && Array.isArray(category.metadata)) {
          const sortNumberMeta = category.metadata.find((item) => item.key === "sortNumber");
          if (sortNumberMeta && sortNumberMeta.value) {
            const num = Number(sortNumberMeta.value);
            return isNaN(num) ? null : num;
          }
        }
        // 如果 metadata 中没有，尝试使用 category.sortNumber 作为后备
        if (category.sortNumber) {
          const num = Number(category.sortNumber);
          return isNaN(num) ? null : num;
        }
        return null;
      };

      const sortNumberA = getSortNumber(a);
      const sortNumberB = getSortNumber(b);

      // 如果都没有 sortNumber，保持原顺序
      if (sortNumberA === null && sortNumberB === null) return 0;
      // 如果只有 A 有 sortNumber，A 排在前面
      if (sortNumberA !== null && sortNumberB === null) return -1;
      // 如果只有 B 有 sortNumber，B 排在前面
      if (sortNumberA === null && sortNumberB !== null) return 1;
      // 如果都有 sortNumber，按数值大小排序
      return sortNumberA! - sortNumberB!;
    });
  }, [categories]);

  return (
    <div className={`bg-white`}>
      <div className="w-full mx-auto">
        {/* 标题和返回按钮 - 仅一级分类显示 */}
        {isFirstLevel && (
          <div className="mb-8 sm:mb-10 lg:mb-12">
            {showBackButton && onBackClick && (
              <button
                onClick={onBackClick}
                className="mb-4 sm:mb-6 flex items-center gap-2 text-gray-600 hover:text-mainColor transition-colors text-sm sm:text-base"
              >
                <i className="ri-arrow-left-line text-xl"></i>
                <span>{t('Back')}</span>
              </button>
            )}
            <h2 className="text-2xl sm:text-3xl md:text-4xl xl:text-5xl font-bold text-black text-center mb-3 sm:mb-4">
              {parentCategoryName || t('Types-of-Hydraulic-Products')}
            </h2>
            {!parentCategoryName && (
              <p className="text-sm sm:text-base xl:text-lg text-gray-600 text-center max-w-6xl mx-auto">
                {t('Types-of-Hydraulic-Products-Desc')}
              </p>
            )}
          </div>
        )}

        {/* 分类网格 */}
        <div className={`grid grid-cols-1 sm:grid-cols-2 ${isFirstLevel ? 'lg:grid-cols-3' : ''} gap-4 sm:gap-5 md:gap-8`}>
          {sortedCategories.map((category, index) => {
            const categoryName = category.translation?.name || category.name;
            
            // 处理 media 字段，可能是 JSON 字符串或直接是 URL
            let categoryImage = '/image/plsyy/product-placeholder.webp';
            if (category.media) {
              try {
                const parsed = typeof category.media === 'string' ? JSON.parse(category.media) : category.media;
                if (Array.isArray(parsed) && parsed.length > 0) {
                  const firstItem = parsed[0] as any;
                  categoryImage = firstItem?.url || (typeof firstItem === 'string' ? firstItem : categoryImage);
                } else if (typeof parsed === 'string') {
                  categoryImage = parsed;
                } else if (parsed && typeof parsed === 'object' && 'url' in parsed) {
                  categoryImage = (parsed as any).url;
                }
              } catch {
                // 如果不是 JSON，直接使用
                categoryImage = category.media as string;
              }
            }
            
            const hasChildren = category.children?.edges && category.children.edges.length > 0;
            const productCount = category.products?.totalCount || 0;

            return (
              <motion.div
                key={category.id}
                initial={{ opacity: 0, y: 30 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.5, delay: index * 0.1 }}
                className="group"
              >
                {hasChildren ? (
                  <button
                    onClick={() => handleCategoryClick(category)}
                    className="relative w-full shadow-md hover:shadow-xl transition-all duration-300 overflow-hidden border border-gray-100 group bg-gradient-to-br from-gray-50 to-gray-100 flex flex-col"
                  >
                    {/* hover时边框从右下角延伸动画 */}
                    <div className="absolute inset-0 pointer-events-none z-30">
                      {/* 右边框 - 从下往上延伸 */}
                      <div className="absolute bottom-0 right-0 w-[2px] h-0 bg-mainColor group-hover:h-full transition-all duration-500 ease-out" style={{ transitionDelay: '0s' }}></div>
                      {/* 下边框 - 从右往左延伸 */}
                      <div className="absolute bottom-0 right-0 w-0 h-[2px] bg-mainColor group-hover:w-full transition-all duration-500 ease-out" style={{ transitionDelay: '0.25s' }}></div>
                      {/* 左边框 - 从下往上延伸 */}
                      <div className="absolute bottom-0 left-0 w-[2px] h-0 bg-mainColor group-hover:h-full transition-all duration-500 ease-out" style={{ transitionDelay: '0.5s' }}></div>
                      {/* 上边框 - 从右往左延伸 */}
                      <div className="absolute top-0 right-0 w-0 h-[2px] bg-mainColor group-hover:w-full transition-all duration-500 ease-out" style={{ transitionDelay: '0.75s' }}></div>
                    </div>
                    <div className="absolute inset-0 bg-gradient-to-br from-white/40 via-transparent to-transparent z-10"></div>
                    {/* 图片容器 - 宽度撑满，高度自适应 */}
                    <div className="relative w-full bg-gray-50 flex items-center justify-center min-h-[200px]">
                      <Image
                        src={categoryImage}
                        alt={categoryName}
                        width={800}
                        height={600}
                        className="w-full h-auto object-contain group-hover:scale-105 transition-transform duration-300"
                        sizes="(max-width: 640px) 100vw, (max-width: 1024px) 50vw, 25vw"
                      />
                    </div>
                    {/* 分类名称 - 覆盖在图片上 */}
                    <div className="absolute bottom-0 left-0 right-0 p-4 sm:p-5 md:p-6 z-20">
                      <h3 className="text-base sm:text-lg md:text-xl text-black text-center group-hover:text-mainColor transition-colors font-medium">
                        {categoryName}
                      </h3>
                    </div>
                    {/* 右下角蓝色L形角标 - 默认显示 */}
                    <div className="absolute bottom-0 right-0 z-20 group-hover:hidden transition-all duration-300">
                      <div className="bg-mainColor w-[30px] h-[2px]"></div>
                      <div className="bg-mainColor w-[2px] h-[30px] absolute bottom-0 right-0"></div>
                    </div>
                    {/* hover时显示向右箭头 */}
                    <div className="absolute bottom-0 right-0 z-20 hidden group-hover:flex items-center justify-center w-[40px] h-[40px] bg-mainColor transition-all duration-300">
                      <i className="ri-arrow-right-line text-white text-xl font-bold"></i>
                    </div>
                  </button>
                ) : (
                  <Link
                    href={`/products/${category.slug}`}
                    className="relative w-full shadow-md hover:shadow-xl transition-all duration-300 overflow-hidden border border-gray-100 group bg-gradient-to-br from-gray-50 to-gray-100 flex flex-col"
                  >
                    {/* hover时边框从右下角延伸动画 */}
                    <div className="absolute inset-0 pointer-events-none z-30">
                      {/* 右边框 - 从下往上延伸 */}
                      <div className="absolute bottom-0 right-0 w-[2px] h-0 bg-mainColor group-hover:h-full transition-all duration-500 ease-out" style={{ transitionDelay: '0s' }}></div>
                      {/* 下边框 - 从右往左延伸 */}
                      <div className="absolute bottom-0 right-0 w-0 h-[2px] bg-mainColor group-hover:w-full transition-all duration-500 ease-out" style={{ transitionDelay: '0.25s' }}></div>
                      {/* 左边框 - 从下往上延伸 */}
                      <div className="absolute bottom-0 left-0 w-[2px] h-0 bg-mainColor group-hover:h-full transition-all duration-500 ease-out" style={{ transitionDelay: '0.5s' }}></div>
                      {/* 上边框 - 从右往左延伸 */}
                      <div className="absolute top-0 right-0 w-0 h-[2px] bg-mainColor group-hover:w-full transition-all duration-500 ease-out" style={{ transitionDelay: '0.75s' }}></div>
                    </div>
                    <div className="absolute inset-0 bg-gradient-to-br from-white/40 via-transparent to-transparent z-10"></div>
                    {/* 图片容器 - 宽度撑满，高度自适应 */}
                    <div className="relative w-full bg-gray-50 flex items-center justify-center min-h-[200px]">
                      <Image
                        src={categoryImage}
                        alt={categoryName}
                        width={800}
                        height={600}
                        className="w-full h-auto object-contain group-hover:scale-105 transition-transform duration-300"
                        sizes="(max-width: 640px) 100vw, (max-width: 1024px) 50vw, 25vw"
                      />
                    </div>
                    {/* 分类名称 - 覆盖在图片上 */}
                    <div className="absolute bottom-0 left-0 right-0 p-4 sm:p-5 md:p-6 z-20">
                      <h3 className="text-base sm:text-lg md:text-xl text-black text-center group-hover:text-mainColor transition-colors font-medium">
                        {categoryName}
                      </h3>
                    </div>
                    {/* 右下角蓝色L形角标 - 默认显示 */}
                    <div className="absolute bottom-0 right-0 z-20 flex flex-col items-end group-hover:hidden transition-all duration-300">
                      <div className="bg-mainColor w-[2px] h-[30px]"></div>
                      <div className="bg-mainColor w-[30px] h-[2px] -mt-[2px]"></div>
                    </div>
                    {/* hover时显示向右箭头 */}
                    <div className="absolute bottom-0 right-0 z-20 hidden group-hover:flex items-center justify-center w-[40px] h-[40px] bg-mainColor transition-all duration-300">
                      <i className="ri-arrow-right-line text-white text-xl font-bold"></i>
                    </div>
                  </Link>
                )}
              </motion.div>
            );
          })}
        </div>
      </div>
    </div>
  );
}

